<template>
	<div class="supplier-sales">
		<div class="container">
			<!-- Header -->
			<div class="page-header flex-center row">
				<h1 class="page-title col">Detail Monitoring Sales</h1>
				<div class="page-header-right col-sm-auto col-xs-12 d-flex justify-content-end">
					<smart-toggle
						v-tooltip="'Date Filter Toggle'"
						v-model="isDateFilterEnabled"
						@input="onChangeDateFilter"
					/>
					<!-- Date Filter -->
					<daterangepicker
						:presets="filterDatePresets"
						:start="since"
						:end="until"
						@selected="setFilterDate"
						:disabled="!isDateFilterEnabled"
					>
					</daterangepicker>
				</div>
			</div>
			<div class="page-content mt-4">
				<!-- Table Menu -->
				<div class="sales-info col-md-5 mb-3" v-if="!isFetching && selectedSales">
					<div class="row well d-flex align-items-center">
						<div class="col-auto">
							<span class="icon-sales text-primary">
								<i class="bi bi-person-lines-fill"></i>
							</span>
						</div>
						<div class="col px-0">
							<div class="sales-name">{{ selectedSales.name }}</div>
							<div class="email-style">{{ selectedSales.email }}</div>
						</div>
						<div class="col-auto">
							<div class="total-label">Total Order</div>
							<div class="fs-16 text-primary text-bold">{{ totalOrder }}</div>
						</div>
					</div>
				</div>
				<shimmer
					v-if="isFetching"
					class="mb-4"
					width="40%"
					height="70px"
				/>
				<div class="row">
					<div class="col-sm-6 d-flex justify-content-start mb-2">
						<!-- Search Keyword -->
						<input
							type="text"
							class="input form-control form-control-sm users-search-bo w-50"
							placeholder="email / username seller"
							autocomplete="off"
							v-model="keyword"
							@input="onKeywordChange"
						/>
					</div>
					<div class="col-sm-6 mb-2">
						<div class="table-filters d-flex align-items-center justify-content-end ml-a">
							<!-- Clear Filter -->
							<button type="button" class="btn btn-plain btn-sm" @click="clearFilter" v-if="isFilterOn">
								{{ $t('Clear Filter') }}
							</button>
							<button
								class="btn btn-light flex-center"
								:class="{ 'is-loading': isDownloading }"
								@click="exportSalesMonitoring"
								v-tooltip="dateFilterTooltip"
								:disabled="!isDateFilterEnabled"
							>
								<i class="material-icons">save_alt</i>
								<span class="mr-2">Download</span>
							</button>
						</div>
					</div>
				</div>
				<div class="user-seller-list table-responsive">
					<table class="user-seller-list-table table table-primary table-striped">
						<thead>
							<tr>
								<th width="25%" class="text-left">
									ID Seller
								</th>
								<th width="20%" class="th-sort text-left" :class="sortClass('name')"
									@click="changeSorting('name')">
									Nama
								</th>
								<th width="15%" class="th-sort text-left" :class="sortClass('email')"
									@click="changeSorting('email')">
									Email
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('total_order')"
									@click="changeSorting('total_order')">
									Total Order
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('total_delivered')"
									@click="changeSorting('total_delivered')">
									Total Delivered
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('total_returned')"
									@click="changeSorting('total_returned')">
									Total Returned
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(item, index) in items" :key="index">
								<td>
									<span @click="copyId(item._id)" role="button">
										{{ item._id.$oid ? item._id.$oid : item._id }}
									</span>
								</td>
								<td>
									<div class="name">
										{{ item.name }}
									</div>
								</td>
								<td>
									{{ item.email }}
								</td>
								<td>
									{{ item.total_order }}
								</td>
								<td>
									{{ item.total_delivered }}
								</td>
								<td>
									{{ item.total_returned }}
								</td>
							</tr>
						</tbody>
					</table>
					<div class="table-fetch-status" v-if="isFetching">
						<div class="is-loading large dark m-auto"></div>
					</div>
					<!-- Empty -->
					<div class="list-empty" v-if="items.length === 0 && isFetching === false">
						<i class="material-icons mr-2">error_outline</i>
						<span v-if="keyword === ''">There is no Seller Found yet.</span>
						<span v-else>No Seller was found</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
	copyToClipboard,
	downloadFile,
	duplicateVar,
	getAxiosErrorMessage,
	getDateRange,
} from '@/lib/helper';
import adminApi from '@/api/admin';
import shimmer from '@/components/Shimmer.vue';
import Daterangepicker from '@/components/Daterangepicker.vue';
import SmartToggle from '@/components/SmartToggle.vue';

const defaultFilterDate = getDateRange();

export default {
	name: 'AdminSales',
	components: {
		shimmer,
		Daterangepicker,
		SmartToggle,
	},
	data() {
		return {
			limit: 25,
			page: 1,
			sort_by: 'created_at',
			sort: 'desc',
			keyword: '',
			filterDate: defaultFilterDate,
			isFetching: false,
			isAll: false,
			items: [],
			selectedSales: null,
			seller_count: 0,
			isDateFilterEnabled: true,
			isDownloading: false,
		};
	},
	methods: {
		resetItems() {
			this.items = [];
			this.page = 1;
		},
		getFetchParams() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
			};

			if (this.isDateFilterEnabled) {
				params.since = startDate;
				params.until = endDate;
			}

			if (this.keyword) {
				params.keyword = this.keyword;
			}

			return params;
		},
		fetchItems(reset = true) {
			const id = this.$route.params.sales_id;
			this.isFetching = true;
			if (reset) {
				this.resetItems();
			}

			const params = this.getFetchParams();

			const callback = (response) => {
				this.isFetching = false;
				let items = response.data?.sellers;

				const currentSellerIds = this.items.map(item => item?._id?.$oid ?? item?._id);
				const unassignedSellers = response.data?.unassigned_sellers;
				if (unassignedSellers && unassignedSellers.length > 0) {
					const sellers = unassignedSellers.filter(seller => !currentSellerIds.includes(seller._id));
					items = [
						...items,
						...sellers,
					];
				}

				this.selectedSales = response.data?.sales;
				this.seller_count = response.data?.page_info?.total_all_sellers;
				if (items?.length > 0) {
					this.page++;
					this.items = this.items.concat(items);
				} else {
					this.isAll = true;
				}
			};

			const errorCallback = (e) => {
				this.isFetching = false;

				const message = getAxiosErrorMessage(e);

				this.$notify({
					group: 'app',
					title: 'Detail Sales',
					text: message ?? 'Failed to Fetch Data',
					type: 'error',
				});
			};

			adminApi.getSalesMonitoringDetail(id, params, callback, errorCallback);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchItems(false);
				}
			}
		},
		changeSorting(key) {
			const isSame = this.sort_by === key;
			this.sort_by = key;
			const sort = isSame && this.sort === 'asc' ? 'desc' : 'asc';
			this.sort = sort;
			// Re-fetch
			this.fetchItems(true);
		},
		sortClass(key) {
			const sameKey = this.sort_by === key;
			const className = sameKey ? `th-sort-${this.sort}` : '';
			return className;
		},
		copyId(item) {
			const salesId = item.$oid ? item.$oid : item;
			copyToClipboard(salesId);
			// toast notification
			this.$notify({
				group: 'app',
				title: 'ID Copied',
				text: salesId,
				type: 'success',
			});
		},
		onKeywordChange() {
			this.ooDelay(() => {
				this.fetchItems(true);
			}, 1000);
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const since = this.moment(this.since);
				const until = this.moment(this.until);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		checkFilterDate() {
			this.isDateFilterEnabled = this.dateFilterEnabled !== 'false';
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const format = 'YYYY-MM-DD';
			const since = this.moment(filterDate.start).format(format);
			const until = this.moment(filterDate.end).format(format);
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchItems(true);
		},
		onChangeDateFilter(value) {
			this.isDateFilterEnabled = value;
			this.fetchItems(true);
		},
		clearFilter() {
			this.keyword = '';
			this.fetchItems(true);
		},
		exportSalesMonitoring() {
			this.isDownloading = true;

			const salesId = this.selectedSales?._id ?? '';

			const params = this.getFetchParams();

			params.type = 'dropshipper';
			params.sales_id = salesId;
			params.is_export = true;

			const callback = (response) => {
				const url = response.data;
				if (url && url !== '') {
					downloadFile(url);
				} else {
					this.$notify({
						group: 'app',
						type: 'warn',
						title: 'Sales Monitoring',
						text: 'Download Failed',
					});
				}
				this.isDownloading = false;
			};

			const errorCallback = () => {
				this.isDownloading = false;
			};

			adminApi.exportSalesMonitoring(params, callback, errorCallback);
		},
	},
	computed: {
		...mapGetters({
			filterDatePresets: 'filterDatePresets',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		dateFilterEnabled() {
			const isDateFilterEnabled = this.$route.query.is_date_filter_enabled;
			return isDateFilterEnabled;
		},
		isFilterOn() {
			return this.keyword !== '';
		},
		dateFilterTooltip() {
			const tooltip = !this.isDateFilterEnabled ? 'Aktifkan Dahulu Filter Tanggal' : '';

			return tooltip;
		},
		totalOrder() {
			const total = this.selectedSales?.total_order ?? 0;

			return total;
		},
	},
	created() {
		this.initFilterDate();
		this.checkFilterDate();
		this.fetchItems();
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style lang="scss" scoped>
.icon-sales {
	font-size: 40px;
}
.fs-16 {
	font-size: 16px;
}
.sales-name {
	font-size: 14px;
	font-weight: 500;
	line-height: 21px;
	letter-spacing: 0em;
	text-align: left;
	color: #2E323D;
}
.email-style {
	font-size: 14px;
	font-weight: 400;
	line-height: 21px;
	letter-spacing: 0em;
	text-align: left;
	color: #80848E;
}
.w-50 {
	width: 50% !important;
}
</style>
